import type {
  CreateProductSetForBudgetsPurchaseForHirerMutation,
  CreateProductSetForBudgetsPurchaseForVisitorMutation,
  DisplayRecommendation,
  TaxCode,
} from '../../generated/graphql-types';

export type BudgetTypes = 'FlexibleBudget' | 'BrandedBudget' | 'PremiumBudget';

export interface BudgetOption {
  title: string;
  budgetId: string;
  budgetName: string;
  budgetType: BudgetTypes;
  budgetValue: number;
  currency: string;
  productId: string;
  tax?: {
    code: TaxCode;
    displayRecommendation: DisplayRecommendation;
    value: number;
  };
  group?: {
    displayName: string;
    key: BudgetTypes;
  };
  tagLine: string;
  inclusions: string[];
  highlights: string[];
}

type CreateProductSetForBudgetsPurchase =
  | CreateProductSetForBudgetsPurchaseForVisitorMutation['createProductSetForBudgetsPurchaseForVisitor']
  | CreateProductSetForBudgetsPurchaseForHirerMutation['createProductSetForBudgetsPurchaseForHirer'];

type AdBudgetItem =
  CreateProductSetForBudgetsPurchase['productSet']['items'][number];

type AdBudgetProductItem = Extract<
  AdBudgetItem,
  { __typename: 'AdBudgetProduct' }
>;

type AdBudgetProductItemPrice = Extract<
  AdBudgetProductItem['price'],
  { __typename: 'Price' }
>;
export type AdBudgetProductItemType = Omit<AdBudgetProductItem, 'price'> & {
  price: AdBudgetProductItemPrice;
};
// This determines if it is a Branded budget
// TODO https://myseek.atlassian.net/browse/AT-7221
// We want to remove this function in the future, since moving forward,
// we would like to depend on the key in group in the budgetOption types in order to differentiate between different budget types.
// However, since for the time being, that group is optional and not mandatory YET, we still need this.
export const isBrandedFeaturesFeaturePresent = (
  adBudgetProduct: AdBudgetProductItemType,
) =>
  Boolean(
    adBudgetProduct.features.find(
      (feature) => feature?.__typename === 'BrandedFeaturesFeature',
    ),
  );

// TODO move this to backend
const mapBudgetOption = (
  adBudgetProduct: AdBudgetProductItemType,
): BudgetOption => {
  const { id: productId, display } = adBudgetProduct;

  if (!display) {
    throw new Error('Budget display is missing');
  }

  const { title, tagline, inclusions, highlights } = display;

  const isMissingRequiredDisplayFields =
    !title || !tagline || !inclusions || !highlights;

  if (isMissingRequiredDisplayFields) {
    throw new Error('Budget display is incomplete');
  }

  const getBudgetType = () => {
    if (adBudgetProduct.group?.key) {
      return adBudgetProduct.group?.key;
    }

    if (isBrandedFeaturesFeaturePresent(adBudgetProduct)) {
      return 'BrandedBudget';
    }

    return 'FlexibleBudget';
  };

  const code = adBudgetProduct.price.tax?.code ?? undefined;
  const displayRecommendation =
    adBudgetProduct.price.tax?.displayRecommendation ?? undefined;
  const value = adBudgetProduct.price.tax?.amount?.value ?? undefined;

  return {
    budgetValue: adBudgetProduct.price.netPrice.value,
    currency: adBudgetProduct.price.netPrice.currency,
    tax:
      code && displayRecommendation && value
        ? {
            code,
            value,
            displayRecommendation,
          }
        : undefined,
    title: title.content,
    budgetId: adBudgetProduct.id,
    budgetName: adBudgetProduct.name,
    budgetType: (adBudgetProduct.group?.key as BudgetTypes) ?? getBudgetType(),
    tagLine: tagline.content,
    inclusions: inclusions.content.map((inclusion) => inclusion.content),
    highlights: highlights.content.map((highlight) => highlight.content),
    productId,
  };
};

export { mapBudgetOption };
